import React from 'react';
import { TextField, InputAdornment, TextFieldProps } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { alpha } from '@mui/material/styles';

interface SearchFieldProps extends Omit<TextFieldProps, 'variant'> {
  placeholder?: string;
}

const SearchField: React.FC<SearchFieldProps> = ({ 
  placeholder = "Search...", 
  InputProps,
  sx,
  ...props 
}) => {
  return (
    <TextField
      fullWidth
      placeholder={placeholder}
      variant="outlined" // Use a standard variant
      size="medium"
      sx={{
        maxWidth: '600px',
        backgroundColor: theme => alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.6 : 0.8),
        borderRadius: theme => theme.shape.borderRadius,
        boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.25 : 0.05)}`,
        transition: 'background-color 220ms ease-out, transform 220ms ease-out, box-shadow 220ms ease-out',
        '&:hover': {
          transform: 'translateY(-1px)',
        },
        '& .MuiOutlinedInput-root': {
          transition: 'border-color 220ms ease-out, box-shadow 220ms ease-out, outline 220ms ease-out',
          '&:hover': {
            borderColor: theme => theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light,
            boxShadow: theme => `0 3px 10px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.35 : 0.08)}`
          },
          '&.Mui-focused': {
            outline: theme => `2px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.7 : 0.3)}`,
            borderColor: theme => theme.palette.primary.main,
            boxShadow: theme => `0 0 0 1px ${theme.palette.primary.main}`
          }
        },
        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
          color: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)',
          transition: 'color 220ms ease-out',
        },
        '&:hover .MuiInputAdornment-root .MuiSvgIcon-root': {
          color: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)',
        },
        '.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root': {
          color: theme => theme.palette.primary.main,
        },
        ...sx
      }}
      InputProps={{
        ...InputProps,
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
      {...props}
    />
  );
};

export default SearchField;